import { AfterContentInit, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import { I18nService } from '@app/core';
import { User, UserInput, OrganizationInput, SlimOrganization, SelectOption, LocaleInput } from '@app/models';
import { ErrorHandlerService, OrganizationService } from '@app/services';

export type ModalResult = SlimOrganization;
export interface ModalPublicProperties {
  user: User;
}

@Component({
  selector: 'app-edit-organization-modal',
  templateUrl: './edit-organization-modal.component.html',
  styleUrls: ['./edit-organization-modal.component.scss'],
  standalone: false
})
export class EditOrganizationModalComponent implements ModalPublicProperties, AfterContentInit {
  @Input() user: User;
  form!: UntypedFormGroup;
  isLoading = false;
  readonly countries: SelectOption[] = [];
  readonly languages: SelectOption[] = [];
  @ViewChild('companyInput', { static: false }) private companyInput: ElementRef<HTMLInputElement>;

  constructor(
    public modal: NgbActiveModal,
    private i18nService: I18nService,
    private formBuilder: UntypedFormBuilder,
    private errorHandlerService: ErrorHandlerService,
    private organizationService: OrganizationService
  ) {
    this.countries = this.i18nService.countriesWithCodes || null;
    this.languages = this.i18nService.supportedLanguagesAsSelectOptions || null;
    this.form = this.formBuilder.group({
      organization: this.formBuilder.group({
        name: null,
        cnpj: null,
        locale: this.formBuilder.group({ country: '', language: '' } as LocaleInput)
      } as OrganizationInput)
    } as UserInput);
  }

  ngAfterContentInit() {
    this.form.setValue({
      organization: {
        name: this.form.get('organization.name').value || this.user.organization.name || null,
        cnpj: (this.form.get('organization.cnpj').value || this.user.organization.cnpj || '').replace(/[^0-9]/g, '') || null,
        locale: {
          country: this.form.get('organization.locale.country').value || this.user.organization.locale?.country || this.user.organization.country || null,
          language: this.form.get('organization.locale.language').value || this.user.organization.locale?.language || null
        }
      }
    } as UserInput);
    setTimeout(() => this.companyInput.nativeElement.focus());
  }

  update() {
    this.isLoading = true;
    this.form.markAllAsTouched();
    const organizationParams = this.form.value;

    if (organizationParams.organization.locale.country !== 'BR') {
      delete organizationParams.organization.cnpj;
    }

    this.organizationService
      .update(organizationParams)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: data => this.modal.close(data as SlimOrganization),
        error: error => this.errorHandlerService.handleValidation(this.form, error)
      });
  }
}
